import { useEffect, useState } from "react";
import { vpAxios, money, VAIPEDE_API, downloadPdf } from "../api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FileText, Printer, DollarSign, X, Minus } from "lucide-react";

export default function MesasPage() {
  const [mesas, setMesas] = useState([]);
  const [edit, setEdit] = useState(null);
  const [ver, setVer] = useState(null); // {mesa, pedidos, total}
  const [fechar, setFechar] = useState(null);

  const load = () => vpAxios.get("/vaipede/mesas").then(r => setMesas(r.data));
  useEffect(() => { load(); }, []);

  const criar = async () => {
    try { await vpAxios.post("/vaipede/mesas", {}); toast.success("Mesa criada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir mesa?")) return;
    await vpAxios.delete(`/vaipede/mesas/${id}`); load();
  };

  const abrirConferencia = async (m) => {
    try {
      const { data } = await vpAxios.get(`/vaipede/mesas/${m.id}/comanda`);
      setVer(data);
    } catch (e) { toast.error("Sem pedidos abertos nesta mesa"); }
  };

  const imprimirMesa = (m) => {
    downloadPdf(`/vaipede/mesas/${m.id}/recibo-termico`, `comanda-mesa-${m.numero}.pdf`)
      .catch(() => toast.error("Sem pedidos abertos para imprimir"));
  };

  return (
    <div data-testid="vaipede-mesas">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black">Mesas</h1>
          <p className="text-sm text-neutral-500">Cadastro e visão de ocupação</p>
        </div>
        <button onClick={criar} className="px-4 py-2.5 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm flex items-center gap-2" data-testid="vaipede-btn-add-mesa">
          <Plus className="w-4 h-4"/> Nova mesa
        </button>
      </div>

      {mesas.length === 0 && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-10 text-center">
          <p className="text-neutral-500">Nenhuma mesa cadastrada.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {mesas.map(m => (
          <div key={m.id} className={`bg-white rounded-2xl border-t-4 ${m.ocupada ? "border-t-[#EA1D2C]" : "border-t-emerald-500"} border border-neutral-200 p-4`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-black text-lg">🪑 {m.nome}</h3>
                <button onClick={() => setEdit(m)} className="text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1 mt-0.5" data-testid={`vaipede-edit-mesa-${m.id}`}>
                  <Pencil className="w-3 h-3"/> {m.capacidade} cadeiras
                </button>
              </div>
              <button onClick={() => excluir(m.id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
            </div>
            <div className={`text-sm font-bold mb-3 ${m.ocupada ? "text-[#EA1D2C]" : "text-emerald-600"}`}>
              {m.ocupada ? `● Ocupada · ${money(m.total_aberto)}` : "● Livre"}
            </div>
            {m.ocupada && (
              <div className="grid grid-cols-3 gap-1">
                <button onClick={() => abrirConferencia(m)} className="p-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold flex flex-col items-center gap-0.5" data-testid={`vaipede-conf-${m.id}`}>
                  <FileText className="w-4 h-4"/> Conferir
                </button>
                <button onClick={() => imprimirMesa(m)} className="p-2 rounded-lg bg-neutral-100 text-neutral-700 text-xs font-bold flex flex-col items-center gap-0.5" data-testid={`vaipede-imp-${m.id}`}>
                  <Printer className="w-4 h-4"/> Imprimir
                </button>
                <button onClick={() => setFechar(m)} className="p-2 rounded-lg bg-[#EA1D2C] text-white text-xs font-bold flex flex-col items-center gap-0.5" data-testid={`vaipede-fech-${m.id}`}>
                  <DollarSign className="w-4 h-4"/> Fechar
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {edit && <EditMesaModal mesa={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }}/>}
      {ver && <ConferenciaModal data={ver} onClose={() => setVer(null)} onImprimir={() => imprimirMesa(ver.mesa)} onFechar={() => { setFechar(ver.mesa); setVer(null); }}/>}
      {fechar && <FecharMesaModal mesa={fechar} onClose={() => setFechar(null)} onDone={() => { setFechar(null); load(); }}/>}
    </div>
  );
}

function EditMesaModal({ mesa, onClose, onSaved }) {
  const [f, setF] = useState({ nome: mesa.nome, capacidade: mesa.capacidade });
  const salvar = async () => {
    try { await vpAxios.put(`/vaipede/mesas/${mesa.id}`, f); toast.success("Mesa atualizada"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4"><h2 className="font-black">Editar mesa</h2><button onClick={onClose}><X className="w-5 h-5"/></button></div>
        <label className="block mb-3">
          <span className="text-xs text-neutral-500">Nome</span>
          <input value={f.nome} onChange={e => setF({...f, nome: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
        </label>
        <label className="block mb-4">
          <span className="text-xs text-neutral-500">Quantidade de cadeiras</span>
          <input type="number" min="1" value={f.capacidade} onChange={e => setF({...f, capacidade: parseInt(e.target.value || 1)})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-mesa-capacidade"/>
        </label>
        <button onClick={salvar} className="w-full py-3 rounded-xl bg-[#EA1D2C] text-white font-bold">Salvar</button>
      </div>
    </div>
  );
}

function ConferenciaModal({ data: initial, onClose, onImprimir, onFechar }) {
  const [data, setData] = useState(initial);
  const [prods, setProds] = useState([]);
  const [showAdd, setShowAdd] = useState(null); // pedido_id p/ adicionar item extra
  const [busca, setBusca] = useState("");

  useEffect(() => {
    vpAxios.get("/vaipede/produtos").then(r => setProds(r.data));
  }, []);

  const recarregar = () => vpAxios.get(`/vaipede/mesas/${data.mesa.id}/comanda`).then(r => setData(r.data));

  const toggleTaxa = async (campo, valor) => {
    try {
      await vpAxios.patch(`/vaipede/mesas/${data.mesa.id}/taxas`, { [campo]: valor });
      recarregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const alterarQtd = async (pedido, produto_id, delta) => {
    const novos = (pedido.itens || []).map(x =>
      x.produto_id === produto_id ? { ...x, quantidade: Math.max(0, (x.quantidade || 0) + delta) } : x
    ).filter(x => x.quantidade > 0);
    try {
      await vpAxios.patch(`/vaipede/pedidos/${pedido.id}/itens`, { itens: novos });
      recarregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const adicionarItem = async (pedido, produto) => {
    const jaTem = (pedido.itens || []).find(x => x.produto_id === produto.id);
    let novos;
    if (jaTem) {
      novos = pedido.itens.map(x => x.produto_id === produto.id ? { ...x, quantidade: x.quantidade + 1 } : x);
    } else {
      novos = [...(pedido.itens || []), {
        produto_id: produto.id, nome: produto.nome, categoria: produto.categoria,
        foto_url: produto.foto_url, preco: produto.preco, quantidade: 1
      }];
    }
    try {
      await vpAxios.patch(`/vaipede/pedidos/${pedido.id}/itens`, { itens: novos });
      toast.success(`${produto.nome} adicionado`);
      setShowAdd(null); recarregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const prodsFiltrados = prods.filter(p => p.ativo !== false && p.nome.toLowerCase().includes(busca.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-black">🪑 {data.mesa.nome} — Conferência</h2>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="flex-1 overflow-auto space-y-3">
          {(data.pedidos || []).map(p => (
            <div key={p.id} className="border border-neutral-200 rounded-xl p-3">
              <div className="flex items-center justify-between text-sm mb-2">
                <b>Pedido #{p.numero}</b>
                <span className="text-neutral-500 text-xs">{new Date(p.created_at).toLocaleTimeString("pt-BR")}</span>
              </div>
              <div className="space-y-1 text-sm">
                {(p.itens || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="flex-1 min-w-0 truncate">{it.nome} <span className="text-neutral-400">· {money(it.preco)}</span></span>
                    <button onClick={() => alterarQtd(p, it.produto_id, -1)} className="p-1 rounded bg-neutral-100" data-testid={`vaipede-conf-dec-${p.id}-${it.produto_id}`}><Minus className="w-3 h-3"/></button>
                    <span className="w-6 text-center font-bold">{it.quantidade}</span>
                    <button onClick={() => alterarQtd(p, it.produto_id, +1)} className="p-1 rounded bg-neutral-100" data-testid={`vaipede-conf-inc-${p.id}-${it.produto_id}`}><Plus className="w-3 h-3"/></button>
                    <span className="w-14 text-right text-xs font-bold">{money(it.preco * it.quantidade)}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setShowAdd(p.id)} className="mt-2 w-full py-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold flex items-center justify-center gap-1" data-testid={`vaipede-conf-add-${p.id}`}>
                <Plus className="w-3 h-3"/> Adicionar item extra (saída)
              </button>
              {showAdd === p.id && (
                <div className="mt-2 bg-neutral-50 rounded-xl p-2 max-h-52 overflow-auto">
                  <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar produto..." className="w-full px-2 py-1.5 rounded-lg border border-neutral-200 text-xs mb-2"/>
                  <div className="grid grid-cols-2 gap-1">
                    {prodsFiltrados.slice(0, 20).map(prod => (
                      <button key={prod.id} onClick={() => adicionarItem(p, prod)} className="text-left bg-white rounded-lg p-2 text-xs border border-neutral-200 hover:border-[#EA1D2C]">
                        <b>{prod.nome}</b>
                        <div className="text-[#EA1D2C] font-bold">{money(prod.preco)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-neutral-200 space-y-1 text-sm">
          {(data.taxa_servico_ativa || data.taxa_couvert_ativa) && (
            <div className="flex items-center justify-between text-neutral-600">
              <span>Subtotal</span>
              <span data-testid="vaipede-conf-subtotal">{money(data.subtotal ?? data.total)}</span>
            </div>
          )}
          {data.taxa_servico_ativa && (
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-neutral-600 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!data.remover_taxa_servico}
                  onChange={e => toggleTaxa("remover_taxa_servico", !e.target.checked)}
                  className="accent-[#EA1D2C]"
                  data-testid="vaipede-conf-toggle-servico"
                />
                <span className={data.remover_taxa_servico ? "line-through text-neutral-400" : ""}>
                  Taxa de serviço{data.taxa_servico_tipo === "percentual" && data.taxa_servico_valor_cfg ? ` (${data.taxa_servico_valor_cfg}%)` : ""}
                </span>
              </label>
              <span
                className={data.remover_taxa_servico ? "text-neutral-400 line-through" : "text-neutral-600"}
                data-testid="vaipede-conf-taxa-servico"
              >
                {data.remover_taxa_servico
                  ? money(data.taxa_servico_tipo === "fixo"
                      ? (data.taxa_servico_valor_cfg || 0)
                      : (data.subtotal || 0) * (data.taxa_servico_valor_cfg || 0) / 100)
                  : money(data.taxa_servico_valor)}
              </span>
            </div>
          )}
          {data.taxa_couvert_ativa && (
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-neutral-600 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!data.remover_taxa_couvert}
                  onChange={e => toggleTaxa("remover_taxa_couvert", !e.target.checked)}
                  className="accent-[#EA1D2C]"
                  data-testid="vaipede-conf-toggle-couvert"
                />
                <span className={data.remover_taxa_couvert ? "line-through text-neutral-400" : ""}>
                  Couvert{data.taxa_couvert_tipo === "percentual" && data.taxa_couvert_valor_cfg
                    ? ` (${data.taxa_couvert_valor_cfg}%)`
                    : (data.num_pessoas > 0 && data.taxa_couvert_valor_cfg)
                      ? ` (${data.num_pessoas}x ${money(data.taxa_couvert_valor_cfg)})`
                      : ""}
                </span>
              </label>
              <span
                className={data.remover_taxa_couvert ? "text-neutral-400 line-through" : "text-neutral-600"}
                data-testid="vaipede-conf-couvert"
              >
                {data.remover_taxa_couvert
                  ? money(data.taxa_couvert_tipo === "percentual"
                      ? (data.subtotal || 0) * (data.taxa_couvert_valor_cfg || 0) / 100
                      : (data.taxa_couvert_valor_cfg || 0) * (data.num_pessoas || 1))
                  : money(data.taxa_couvert_valor)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-lg pt-1">
            <b>Total da mesa</b>
            <b className="text-[#EA1D2C]" data-testid="vaipede-conf-total">{money(data.total_final ?? data.total)}</b>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={onImprimir} className="py-3 rounded-xl bg-neutral-100 font-bold text-sm flex items-center justify-center gap-2"><Printer className="w-4 h-4"/> Imprimir 80mm</button>
          <button onClick={onFechar} className="py-3 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm flex items-center justify-center gap-2"><DollarSign className="w-4 h-4"/> Fechar mesa</button>
        </div>
      </div>
    </div>
  );
}

function FecharMesaModal({ mesa, onClose, onDone }) {
  const [pagamentos, setPagamentos] = useState([{ forma: "dinheiro", valor: "" }]);
  const [comanda, setComanda] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    vpAxios.get(`/vaipede/mesas/${mesa.id}/comanda`).then(r => setComanda(r.data));
  }, [mesa.id]);

  const total = Number(comanda?.total_final || comanda?.total || 0);
  const totalPago = pagamentos.reduce((s, p) => s + Number(p.valor || 0), 0);
  const troco = Math.max(0, totalPago - total);
  const faltando = Math.max(0, total - totalPago);

  const addLinha = () => setPagamentos(ps => [...ps, { forma: "dinheiro", valor: "" }]);
  const rmLinha = (i) => setPagamentos(ps => ps.filter((_, idx) => idx !== i));
  const setLinha = (i, k, v) => setPagamentos(ps => ps.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  const preencherRestante = (i) => {
    const rest = total - pagamentos.reduce((s, p, idx) => idx === i ? s : s + Number(p.valor || 0), 0);
    if (rest > 0) setLinha(i, "valor", rest.toFixed(2));
  };

  const fechar = async () => {
    if (totalPago < total - 0.01) { toast.error(`Faltam ${money(faltando)} para completar o pagamento`); return; }
    setLoading(true);
    try {
      const pagList = pagamentos.filter(p => Number(p.valor || 0) > 0).map(p => ({ forma: p.forma, valor: Number(p.valor) }));
      const { data } = await vpAxios.post(`/vaipede/mesas/${mesa.id}/fechar-completo`, {
        pagamentos: pagList,
        forma_pagamento: pagList[0]?.forma || "dinheiro",
      });
      toast.success(`Mesa liberada! Total: ${money(data.total)}${data.troco > 0 ? ` · Troco: ${money(data.troco)}` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[95vh] overflow-auto">
        <h2 className="font-black mb-1">Fechar {mesa.nome}</h2>
        <p className="text-xs text-neutral-500 mb-3">Divida o pagamento em quantas formas quiser.</p>

        <div className="bg-neutral-50 rounded-xl p-3 mb-3 text-sm">
          <div className="flex items-center justify-between"><span>Total da mesa</span><b className="text-[#EA1D2C]">{money(total)}</b></div>
          {comanda?.taxa_servico_valor > 0 && !comanda.remover_taxa_servico && <div className="flex items-center justify-between text-xs text-neutral-500 mt-1"><span>+ Taxa serviço</span><span>{money(comanda.taxa_servico_valor)}</span></div>}
          {comanda?.taxa_couvert_valor > 0 && !comanda.remover_taxa_couvert && <div className="flex items-center justify-between text-xs text-neutral-500"><span>+ Couvert</span><span>{money(comanda.taxa_couvert_valor)}</span></div>}
        </div>

        <div className="space-y-2 mb-3">
          {pagamentos.map((p, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`vaipede-fech-linha-${i}`}>
              <select value={p.forma} onChange={e => setLinha(i, "forma", e.target.value)} className="px-2 py-2 rounded-xl border border-neutral-200 text-sm">
                <option value="dinheiro">💵 Dinheiro</option>
                <option value="pix">📱 Pix</option>
                <option value="cartao_credito">💳 Crédito</option>
                <option value="cartao_debito">💳 Débito</option>
                <option value="maquininha">🏪 Máquina física</option>
              </select>
              <input type="number" step="0.01" value={p.valor} onChange={e => setLinha(i, "valor", e.target.value)}
                onBlur={e => { if (!e.target.value) preencherRestante(i); }}
                placeholder="R$ 0,00" className="flex-1 px-3 py-2 rounded-xl border border-neutral-200 text-sm" data-testid={`vaipede-fech-valor-${i}`}/>
              {pagamentos.length > 1 && <button onClick={() => rmLinha(i)} className="p-2 text-red-500" data-testid={`vaipede-fech-rm-${i}`}><Trash2 className="w-4 h-4"/></button>}
            </div>
          ))}
          <button onClick={addLinha} className="w-full py-2 rounded-xl border-2 border-dashed border-neutral-300 text-neutral-600 text-sm font-bold flex items-center justify-center gap-1" data-testid="vaipede-fech-add">
            <Plus className="w-4 h-4"/> Adicionar forma de pagamento
          </button>
        </div>

        <div className="bg-neutral-50 rounded-xl p-3 mb-3 text-sm space-y-1">
          <div className="flex items-center justify-between"><span>Total pago</span><b>{money(totalPago)}</b></div>
          {faltando > 0 && <div className="flex items-center justify-between text-red-600 font-bold"><span>Faltando</span><span>{money(faltando)}</span></div>}
          {troco > 0 && <div className="flex items-center justify-between text-emerald-700 font-black"><span>Troco</span><span>{money(troco)}</span></div>}
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-neutral-100 font-bold text-sm">Cancelar</button>
          <button onClick={fechar} disabled={loading || faltando > 0} className="flex-1 py-3 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm disabled:opacity-60" data-testid="vaipede-btn-confirmar-fechar">
            {loading ? "Fechando..." : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}
